import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Heading,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Badge,
  Avatar,
  Flex,
  Spacer,
  Spinner,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Alert,
  AlertIcon,
  useToast,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../contexts/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  created_at: string;
  is_active?: boolean;
  company_name?: string;
}

interface InviteFormState {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(firstName: string, lastName: string): string {
  const f = firstName?.trim()[0] ?? '';
  const l = lastName?.trim()[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function roleBadgeColor(role: UserRole): string {
  switch (role) {
    case 'super_admin':
      return 'purple';
    case 'account_admin':
      return 'blue';
    default:
      return 'gray';
  }
}

function roleDisplayLabel(role: UserRole): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'account_admin':
      return 'Admin';
    default:
      return 'User';
  }
}

// ─── Three-dot menu icon (inline SVG ellipsis) ────────────────────────────────

const EllipsisIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const TeamManagement: React.FC = () => {
  const {
    user,
    isSuperAdmin,
    canManageUsers,
    userProfile,
    clients,
    createUser,
    updateUserRole,
    removeUser,
    resetPassword,
    startImpersonation,
  } = useAuth();

  const toast = useToast();
  const navigate = useNavigate();

  // ── Company selector (super_admin only) ────────────────────────────────────

  // Unique sorted company names from the clients list
  const allCompanies = useMemo(() => {
    const names = Array.from(new Set(clients.map((c) => c.company_name))).sort();
    return names;
  }, [clients]);

  const [selectedCompany, setSelectedCompany] = useState<string>('');

  // Invite modal
  const {
    isOpen: isInviteOpen,
    onOpen: onInviteOpen,
    onClose: onInviteClose,
  } = useDisclosure();

  // Remove confirmation modal
  const {
    isOpen: isRemoveOpen,
    onOpen: onRemoveOpen,
    onClose: onRemoveClose,
  } = useDisclosure();

  // ── State ──────────────────────────────────────────────────────────────────

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [inviteForm, setInviteForm] = useState<InviteFormState>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user' as UserRole,
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  // ── Effective company: super_admin picks via selector, others use their own ──
  const effectiveCompany = isSuperAdmin
    ? selectedCompany
    : (userProfile?.company_name ?? '');

  // ── Fetch members ──────────────────────────────────────────────────────────

  const fetchMembers = useCallback(async () => {
    if (!user?.id || !effectiveCompany) return;

    setIsLoading(true);
    setFetchError(null);

    try {
      const companyParam = encodeURIComponent(effectiveCompany);
      const res = await fetch(`${SERVER_URL}/api/users?company=${companyParam}`, {
        headers: {
          'user-id': user.id,
        },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? `Error ${res.status}: Failed to load team members`);
      }

      const data = await res.json();
      setMembers(data.users ?? []);
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to load team members';
      setFetchError(msg);
      toast({
        title: 'Error loading team',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, effectiveCompany, toast]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers, selectedCompany]);

  // ── Invite handlers ────────────────────────────────────────────────────────

  const handleInviteOpen = () => {
    setInviteForm({ email: '', firstName: '', lastName: '', role: 'user' as UserRole });
    setInviteError(null);
    onInviteOpen();
  };

  const handleInviteSubmit = async () => {
    setInviteError(null);

    const emailTrimmed = inviteForm.email.trim();

    if (!emailTrimmed) {
      setInviteError('Email is required.');
      return;
    }

    if (!isValidEmail(emailTrimmed)) {
      setInviteError('Please enter a valid email address.');
      return;
    }

    const duplicate = members.some(
      (m) => m.email.toLowerCase() === emailTrimmed.toLowerCase()
    );
    if (duplicate) {
      setInviteError('A member with this email already exists in your team.');
      return;
    }

    setInviteSubmitting(true);

    try {
      const { error } = await createUser({
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        companyName: effectiveCompany,
        email: emailTrimmed,
        role: inviteForm.role,
      });

      if (error) {
        throw new Error(error?.message ?? error ?? 'Failed to invite member');
      }

      toast({
        title: 'Invitation sent',
        description: `An invite has been sent to ${emailTrimmed}.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      onInviteClose();
      fetchMembers();
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to invite member';
      setInviteError(msg);
      toast({
        title: 'Invite failed',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setInviteSubmitting(false);
    }
  };

  // ── Role change handler ────────────────────────────────────────────────────

  const handleRoleChange = async (memberId: string, newRole: UserRole) => {
    try {
      const { error } = await updateUserRole(memberId, newRole);

      if (error) {
        throw new Error(error?.message ?? error ?? 'Failed to update role');
      }

      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );

      toast({
        title: 'Role updated',
        description: `Role changed to ${roleDisplayLabel(newRole)}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to update role';
      toast({
        title: 'Role update failed',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // ── Remove handlers ────────────────────────────────────────────────────────

  const handleRemoveClick = (member: TeamMember) => {
    setMemberToRemove(member);
    onRemoveOpen();
  };

  const handleRemoveConfirm = async () => {
    if (!memberToRemove) return;

    setRemoveSubmitting(true);

    try {
      const { error } = await removeUser(memberToRemove.id);

      if (error) {
        throw new Error(error?.message ?? error ?? 'Failed to remove member');
      }

      toast({
        title: 'Member removed',
        description: `${memberToRemove.first_name} ${memberToRemove.last_name} has been removed.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      onRemoveClose();
      setMemberToRemove(null);
      fetchMembers();
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to remove member';
      toast({
        title: 'Remove failed',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRemoveSubmitting(false);
    }
  };

  // ── Password reset handler ─────────────────────────────────────────────────

  const handlePasswordReset = async (member: TeamMember) => {
    try {
      const { error } = await resetPassword(member.email);

      if (error) {
        throw new Error(typeof error === 'string' ? error : 'Failed to send reset email');
      }

      toast({
        title: 'Password reset sent',
        description: `Password reset email sent to ${member.email}.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to send password reset';
      toast({
        title: 'Reset failed',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // ── Action menu disabled logic ─────────────────────────────────────────────

  const isActionDisabled = (member: TeamMember): boolean => {
    if (member.id === user?.id) return true;
    if (member.role === 'super_admin' && !isSuperAdmin) return true;
    return false;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const companyName = effectiveCompany || 'your company';

  return (
    <Box p={6}>
      {/* ── Super Admin: Company Selector ── */}
      {isSuperAdmin && allCompanies.length > 0 && (
        <HStack mb={5} spacing={3} align="center">
          <Text fontWeight="semibold" color="gray.600" whiteSpace="nowrap">
            Viewing company:
          </Text>
          <Select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            maxW="280px"
            size="sm"
            borderRadius="md"
            fontWeight="500"
          >
            <option value="" disabled>Select a company...</option>
            {allCompanies.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </Select>
        </HStack>
      )}

      {/* ── Header ── */}
      <Flex align="center" mb={6}>
        <HStack spacing={3}>
          <Heading size="lg" color="gray.800">
            Team Members
          </Heading>
          {!isLoading && (
            <Badge
              colorScheme="blue"
              borderRadius="full"
              px={3}
              py={1}
              fontSize="sm"
              fontWeight="semibold"
            >
              {members.length}
            </Badge>
          )}
        </HStack>
        <Spacer />
        {canManageUsers && (
          <Button
            colorScheme="blue"
            size="md"
            onClick={handleInviteOpen}
            leftIcon={<Text fontSize="lg" lineHeight={1}>+</Text>}
          >
            Invite Member
          </Button>
        )}
      </Flex>

      {/* ── Super admin: no company selected yet ── */}
      {isSuperAdmin && !selectedCompany && (
        <Center py={16}>
          <VStack spacing={3} textAlign="center">
            <Text fontSize="4xl">🏢</Text>
            <Heading size="md" color="gray.600">Select a company</Heading>
            <Text color="gray.500">Use the dropdown above to pick a company and view its team.</Text>
          </VStack>
        </Center>
      )}

      {/* ── Loading ── */}
      {isLoading && !!effectiveCompany && (
        <Center py={16}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500">Loading team members…</Text>
          </VStack>
        </Center>
      )}

      {/* ── Fetch error ── */}
      {!isLoading && fetchError && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {fetchError}
        </Alert>
      )}

      {/* ── Empty state ── */}
      {!isLoading && !fetchError && members.length === 0 && (
        <Center py={16}>
          <VStack spacing={3} textAlign="center">
            <Text fontSize="4xl">👥</Text>
            <Heading size="md" color="gray.600">
              No team members yet
            </Heading>
            <Text color="gray.500" maxW="sm">
              {canManageUsers
                ? `You haven't added any members to your team. Click "+ Invite Member" to get started.`
                : 'Your team currently has no members.'}
            </Text>
          </VStack>
        </Center>
      )}

      {/* ── Members table ── */}
      {!isLoading && !fetchError && members.length > 0 && (
        <TableContainer
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
          boxShadow="sm"
        >
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th>Member</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Joined</Th>
                {canManageUsers || isSuperAdmin ? <Th /> : null}
              </Tr>
            </Thead>
            <Tbody>
              {members.map((member) => {
                const fullName = `${member.first_name} ${member.last_name}`.trim() || member.email;
                const initials = getInitials(member.first_name, member.last_name);
                const isCurrentUser = member.id === user?.id;
                const actionDisabled = isActionDisabled(member);

                return (
                  <Tr key={member.id} _hover={{ bg: 'gray.50' }} transition="background 0.15s">
                    {/* Member column */}
                    <Td>
                      <HStack spacing={3}>
                        <Avatar
                          size="sm"
                          name={fullName}
                          getInitials={() => initials}
                          bg={roleBadgeColor(member.role) + '.400'}
                          color="white"
                        />
                        <VStack align="start" spacing={0}>
                          <HStack spacing={2}>
                            <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                              {fullName}
                            </Text>
                            {isCurrentUser && (
                              <Badge colorScheme="green" fontSize="xs">
                                You
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            {member.email}
                          </Text>
                        </VStack>
                      </HStack>
                    </Td>

                    {/* Role column */}
                    <Td>
                      {canManageUsers && !isCurrentUser ? (
                        <Select
                          size="sm"
                          value={member.role}
                          onChange={(e) =>
                            handleRoleChange(member.id, e.target.value as UserRole)
                          }
                          borderColor="gray.300"
                          maxW="160px"
                          isDisabled={
                            member.role === 'super_admin' && !isSuperAdmin
                          }
                        >
                          <option value="user">User</option>
                          <option value="account_admin">Admin</option>
                          {isSuperAdmin && (
                            <option value="super_admin">Super Admin</option>
                          )}
                        </Select>
                      ) : (
                        <Badge
                          colorScheme={roleBadgeColor(member.role)}
                          borderRadius="full"
                          px={3}
                          py={1}
                          fontSize="xs"
                          textTransform="capitalize"
                        >
                          {roleDisplayLabel(member.role)}
                        </Badge>
                      )}
                    </Td>

                    {/* Status column */}
                    <Td>
                      <Badge
                        colorScheme={member.is_active !== false ? 'green' : 'orange'}
                        borderRadius="full"
                        px={3}
                        py={1}
                        fontSize="xs"
                      >
                        {member.is_active !== false ? 'Active' : 'Pending'}
                      </Badge>
                    </Td>

                    {/* Joined column */}
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(member.created_at)}
                      </Text>
                    </Td>

                    {/* Actions column */}
                    {(canManageUsers || isSuperAdmin) && (
                      <Td isNumeric>
                        <Tooltip
                          label={
                            isCurrentUser
                              ? 'Cannot manage your own account here'
                              : member.role === 'super_admin' && !isSuperAdmin
                              ? 'Cannot manage a Super Admin'
                              : undefined
                          }
                          isDisabled={!actionDisabled}
                          placement="left"
                        >
                          <Box display="inline-block">
                            <Menu placement="bottom-end">
                              <MenuButton
                                as={IconButton}
                                icon={<EllipsisIcon />}
                                variant="ghost"
                                size="sm"
                                aria-label="Member actions"
                                isDisabled={actionDisabled}
                                color="gray.500"
                                _hover={{ bg: 'gray.100', color: 'gray.700' }}
                              />
                              <MenuList fontSize="sm" minW="180px" shadow="lg">
                                {isSuperAdmin && (
                                  <MenuItem
                                    onClick={() => {
                                      const ok = startImpersonation({
                                        userId: member.id,
                                        name: `${member.first_name} ${member.last_name}`.trim() || member.email,
                                        email: member.email,
                                        company: (member.company_name ?? effectiveCompany) as string,
                                      });
                                      if (!ok) {
                                        toast({
                                          title: 'Impersonation failed',
                                          description: `Company "${member.company_name ?? effectiveCompany}" not found in clients list.`,
                                          status: 'error',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                        return;
                                      }
                                      navigate('/');
                                    }}
                                    icon={<Text fontSize="sm">👤</Text>}
                                    color="purple.600"
                                    _hover={{ bg: 'purple.50' }}
                                  >
                                    Impersonate
                                  </MenuItem>
                                )}
                                <MenuItem
                                  onClick={() => handlePasswordReset(member)}
                                  icon={
                                    <Text fontSize="sm">🔑</Text>
                                  }
                                >
                                  Send Password Reset
                                </MenuItem>
                                <MenuItem
                                  onClick={() => handleRemoveClick(member)}
                                  color="red.500"
                                  icon={
                                    <Text fontSize="sm">🗑️</Text>
                                  }
                                  _hover={{ bg: 'red.50' }}
                                >
                                  Remove Member
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Box>
                        </Tooltip>
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ── Invite Member Modal ── */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Modal isOpen={isInviteOpen} onClose={onInviteClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Text>Invite New Member</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4}>
              {inviteError && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {inviteError}
                </Alert>
              )}

              {/* Email */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="semibold">
                  Email Address
                </FormLabel>
                <Input
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  autoFocus
                />
              </FormControl>

              {/* First Name */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold">
                  First Name{' '}
                  <Text as="span" color="gray.400" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Jane"
                  value={inviteForm.firstName}
                  onChange={(e) =>
                    setInviteForm((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                />
              </FormControl>

              {/* Last Name */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold">
                  Last Name{' '}
                  <Text as="span" color="gray.400" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Smith"
                  value={inviteForm.lastName}
                  onChange={(e) =>
                    setInviteForm((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                />
              </FormControl>

              {/* Role */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold">
                  Role
                </FormLabel>
                <Select
                  value={inviteForm.role}
                  onChange={(e) =>
                    setInviteForm((prev) => ({
                      ...prev,
                      role: e.target.value as UserRole,
                    }))
                  }
                >
                  <option value="user">User</option>
                  <option value="account_admin">Admin</option>
                  {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {inviteForm.role === 'user'
                    ? 'Read-only access to team and data.'
                    : inviteForm.role === 'account_admin'
                    ? 'Can manage team members and settings for this account.'
                    : 'Full platform access across all accounts.'}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3} w="full" justify="flex-end">
              <Button variant="ghost" onClick={onInviteClose} isDisabled={inviteSubmitting}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleInviteSubmit}
                isLoading={inviteSubmitting}
                loadingText="Sending invite…"
              >
                Send Invite
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ── Remove Confirmation Modal ── */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Modal isOpen={isRemoveOpen} onClose={onRemoveClose} size="sm" isCentered>
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>Remove Member</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {memberToRemove && (
              <VStack spacing={4} align="start">
                <HStack spacing={3}>
                  <Avatar
                    size="md"
                    name={`${memberToRemove.first_name} ${memberToRemove.last_name}`.trim() || memberToRemove.email}
                    getInitials={() =>
                      getInitials(memberToRemove.first_name, memberToRemove.last_name)
                    }
                    bg={roleBadgeColor(memberToRemove.role) + '.400'}
                    color="white"
                  />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" fontSize="md">
                      {`${memberToRemove.first_name} ${memberToRemove.last_name}`.trim() ||
                        memberToRemove.email}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {memberToRemove.email}
                    </Text>
                  </VStack>
                </HStack>

                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  <Text>
                    Remove{' '}
                    <Text as="strong">
                      {memberToRemove.first_name || memberToRemove.email}
                    </Text>{' '}
                    from{' '}
                    <Text as="strong">{companyName}</Text>? This action cannot be
                    undone.
                  </Text>
                </Alert>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3} w="full" justify="flex-end">
              <Button
                variant="ghost"
                onClick={onRemoveClose}
                isDisabled={removeSubmitting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRemoveConfirm}
                isLoading={removeSubmitting}
                loadingText="Removing…"
              >
                Remove
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TeamManagement;
